import { getCurrentScope, onScopeDispose, type Ref } from 'vue'
import { useConvexMutation } from 'convex-vue'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'

const TYPING_THROTTLE_MS = 2000
// Clear the indicator after this long without a keystroke - the user paused
// without sending, so the other side shouldn't keep seeing "typing…".
export const TYPING_IDLE_MS = 4000

/**
 * Reports that the current user is typing in a room, throttled so callers
 * can invoke `notifyTyping()` on every keystroke without spamming the
 * mutation. The indicator is cleared automatically after TYPING_IDLE_MS
 * without typing, and when the page is closed or navigated away. Callers MUST still
 * call `stopTyping()` when the user sends or the component unmounts - a stale
 * `typingAt` alone isn't enough to clear a viewer's badge, since their query
 * only re-runs on a data change, not just because time passed (see
 * convex/chatRooms.ts's setTyping/clearTyping doc comments).
 */
export function useTypingIndicator(roomId: Ref<string | undefined>, userId: Ref<string | undefined>) {
  const { mutate: setTyping } = useConvexMutation(api.chatRooms.setTyping)
  const { mutate: clearTypingMutation } = useConvexMutation(api.chatRooms.clearTyping)

  let lastSentAt = 0
  let hasPendingTyping = false
  let idleTimer: ReturnType<typeof setTimeout> | null = null

  const clearIdleTimer = () => {
    if (idleTimer) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
  }

  const stopTyping = () => {
    clearIdleTimer()
    if (!roomId.value || !userId.value || !hasPendingTyping) return
    hasPendingTyping = false
    lastSentAt = 0

    clearTypingMutation({
      roomId: roomId.value as Id<'chatRooms'>,
      userId: userId.value as Id<'users'>
    }).catch(() => {
      // Best-effort - a failed clear just leaves the badge to expire via staleness
    })
  }

  const notifyTyping = () => {
    if (!roomId.value || !userId.value) return

    // Every keystroke pushes the idle timeout back, even throttled ones.
    clearIdleTimer()
    idleTimer = setTimeout(stopTyping, TYPING_IDLE_MS)

    const now = Date.now()
    if (now - lastSentAt < TYPING_THROTTLE_MS) return
    lastSentAt = now
    hasPendingTyping = true

    setTyping({
      roomId: roomId.value as Id<'chatRooms'>,
      userId: userId.value as Id<'users'>
    }).catch(() => {
      // Best-effort - a failed typing ping isn't worth surfacing to the user
    })
  }

  // Closing the tab or browser skips component unmount, so clear on the way
  // out too. Best-effort: if the request never lands, viewers still drop the
  // badge once typingAt goes stale. Deliberately NOT tied to the tab merely
  // being hidden - switching tabs mid-reply (e.g. to look something up) isn't
  // leaving, and the idle timeout already clears a paused typer.
  if (typeof window !== 'undefined' && getCurrentScope()) {
    window.addEventListener('pagehide', stopTyping)
    onScopeDispose(() => {
      window.removeEventListener('pagehide', stopTyping)
      clearIdleTimer()
    })
  }

  return { notifyTyping, stopTyping }
}
